import dayjs, {type Dayjs, isDayjs} from 'dayjs';

import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import {useLocaleStore} from '../stores/localeStore';
import type {DateRange} from '../api/models/dateTime/dateRange';

dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(weekday);
dayjs.extend(updateLocale);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
// isoWeeksInYear relies on isLeapYear, so it has to be extended first.
dayjs.extend(isLeapYear);
dayjs.extend(isoWeeksInYear);

export type DateInput = dayjs.Dayjs | Date | string;

/**
 * Returns a dayjs object from any valid dayjs input
 * @param date
 */
export function normalizeDate(date: DateInput) {
    if (isDayjs(date)) {
        return date;
    }

    return dayjs(date);
}

/**
 * Returns a dayjs object with the current valid dayjs locale.
 */
export function localeAware(date: DateInput) {
    const {dayjsLocale} = useLocaleStore();
    return normalizeDate(date).locale(dayjsLocale);
}

/**
 * Formats the date and time using the current locale.
 * @example
 * de: 22.08.2024 14:48
 * en: 08/22/2024 2:48 PM
 * @param date
 */
export function getFormattedDateAndTime(date: DateInput) {
    const normalized = localeAware(date);

    return normalized.format('L LT');
}

/**
 * Formats the date using the current locale.
 * @example
 * de: 22.08.2024
 * en: 08/22/2024
 */
export function getFormattedDate(date: DateInput) {
    const normalized = localeAware(date);

    return normalized.format('L');
}

/**
 * Formats the date using the current locale with adding the current weekday.
 * @example
 * de: Di 22.08.25
 * en: Tu 08/22/25
 */
export function getFormattedWeekdayDate(date: DateInput) {
    const normalized = localeAware(date);

    const formattedDate = normalized.format('L');
    const weekday = normalized.format('dd');

    return `${weekday} ${formattedDate.slice(0, 6) + formattedDate.slice(8, 10)}`;
}

/**
 * Formats the time using the current locale.
 * @example
 * de: 14:48
 * en: 2:48 PM
 */
export function getFormattedTime(date: DateInput) {
    const normalized = normalizeDate(date);

    return normalized.format('LT');
}

/**
 * Returns a date in the specified timezone (or the guessed timezone of the user if not passed)
 * that represents the same date and time as the input date.
 * @example
 * toLocaleDateTime('2024-08-22T14:37:13.729Z', 'Europe/Berlin').toDate()
 * // returns 'Thu Aug 22 2024 14:37:13 GMT+0200 (Mitteleuropäische Sommerzeit)'
 */
export function toLocalDateTime(date: DateInput, timeZone: string | null = null) {
    timeZone ??= dayjs.tz.guess();
    if (!date) {
        return null;
    }
    return dayjs.tz(date, timeZone);
}

/**
 * Compares two dates using the dayjs.isSame function.
 * @example
 * isSame('24-08-22T12:00', '24-08-22T16:00', 'day')
 * // returns true
 */
export function isSame(date1: DateInput, date2: DateInput, unit: dayjs.OpUnitType): boolean {
    if (date1 === date2) {
        return true;
    }
    return normalizeDate(date1).isSame(normalizeDate(date2), unit);
}

/**
 * Receives an array of timepoints, eleminates duplicates and sorts them
 * @param timePoints A array of time points to be uniquely sorted
 * @returns A array of unique sorted time points
 */
export function getUniqueSortedTimes(timePoints: DateInput[]) {
    const normalizedTimePoints = timePoints.map(timePoint => normalizeDate(timePoint));

    return Array.from(new Set(normalizedTimePoints.map(timePoint => timePoint.toISOString())))
        .map(timePoint => normalizeDate(timePoint))
        .sort(getDateSortCompareFn(timePoint => timePoint));
}

/**
 * Returns a function that can be used within {@link Array#sort} to sort by date.
 * @param dateSelector A selector function that returns the date property which should be
 * sorted by.
 * @param sortOrder
 */
export function getDateSortCompareFn<T>(
    dateSelector: (item: T) => DateInput,
    sortOrder: 'asc' | 'desc' = 'asc'
) {
    const getDate = (item: T) => normalizeDate(dateSelector(item));

    switch (sortOrder) {
        case 'asc':
            return (left: T, right: T) => getDate(left).unix() - getDate(right).unix();
        case 'desc':
            return (left: T, right: T) => getDate(right).unix() - getDate(left).unix();
    }
}

export const weekdaysShort = () => dayjs.weekdaysShort();

export function parseTime(str: string) {
    // Match "HH:mm" or "HH:mm:ss"
    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(str);
    if (!match) return;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = match[3] ? parseInt(match[3], 10) : 0;

    if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        Number.isNaN(seconds) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59 ||
        seconds < 0 ||
        seconds > 59
    ) {
        return;
    }

    return {hours, minutes, seconds};
}

/**
 * Updates the passed {@param date} with the parsed time components of {@param time}.
 * If the time component can't be parsed to a valid time, the date will be returned unchanged.
 * @param date The date to update
 * @param time The time in the format 'HH:mm' (ex: '08:00')
 */
export function updateTimeComponents(date: DateInput, time: string): Dayjs {
    const normalized = normalizeDate(date);
    const result = parseTime(time);
    if (result) {
        return normalized
            .set('hours', result.hours)
            .set('minutes', result.minutes)
            .set('seconds', 0)
            .set('milliseconds', 0);
    }
    return normalized;
}

/**
 * Determines whether the two date ranges passed have an overlap.
 * @returns
 */
export function isInRange(from1: DateInput, to1: DateInput, from2: DateInput, to2: DateInput) {
    const from1Normalized = normalizeDate(from1);
    const to1Normalized = normalizeDate(to1);
    const from2Normalized = normalizeDate(from2);
    const to2Normalized = normalizeDate(to2);

    return from1Normalized.isBefore(to2Normalized) && from2Normalized.isBefore(to1Normalized);
}

/**
 *
 * @param firstDate date that should be checked if it's before the second date
 * @param secondDate date that is referenced
 * @returns boolean if first date is before second date
 */
export function isBefore(firstDate: DateInput, secondDate: DateInput) {
    return normalizeDate(firstDate).isBefore(normalizeDate(secondDate));
}

/**
 *
 * @param firstDate date that should be checked if it's after the second date
 * @param secondDate date that is referenced
 * @returns boolean if first date is after second date
 */
export function isAfter(firstDate: DateInput, secondDate: DateInput) {
    return normalizeDate(firstDate).isAfter(normalizeDate(secondDate));
}

/**
 * Formats the input into a suitable format without timezone for API requests
 */
export function getFormattedApiDateTime(date: DateInput): string {
    const normalized = normalizeDate(date);

    return normalized.format('YYYY-MM-DDTHH:mm:ss');
}

export function addDays(date: DateInput, days: number): Date {
    const normalized = normalizeDate(date);

    return normalized.add(days, 'day').toDate();
}

export function startOfDay(date: DateInput): Date {
    const normalized = normalizeDate(date);

    return normalized.startOf('day').toDate();
}

/**
 * A date range is considered "in the past" when its start lies before the
 * beginning of the reference day (defaults to now). Single source of truth for
 * the past-date detection used by the booking store's auto-shift and by the
 * resource usage query guard.
 */
export function isDateRangeInPast(range: DateRange, now: DateInput = dayjs()): boolean {
    return range.from.isBefore(startOfDay(now));
}

export function addTime(date: DateInput, time: number, unit: 'day' | 'hour' | 'minute'): Date {
    const normalized = normalizeDate(date);

    return normalized.add(time, unit).toDate();
}

/**
 * Formats the input into a suitable format without timezone for API requests
 * @example
 * returns '2024-06-10'
 */
export function getFormattedApiDate(date: DateInput): string {
    const normalized = normalizeDate(date);

    return normalized.format('YYYY-MM-DD');
}

/**
 * Returns the earlier of two time strings in HH:mm:ss format.
 * @param time1 Time string in format 'HH:mm:ss' (e.g. '08:30:00')
 * @param time2 Time string in format 'HH:mm:ss' (e.g. '23:45:00')
 * @returns The earlier time string
 */
export function getEarlierTime(time1: string, time2: string): string {
    const t1 = dayjs(`1970-01-01T${time1}`);
    const t2 = dayjs(`1970-01-01T${time2}`);
    return t1.isBefore(t2) ? time1 : time2;
}

/**
 * Returns the later of two time strings in HH:mm:ss format.
 * @param time1 Time string in format 'HH:mm:ss' (e.g. '08:30:00')
 * @param time2 Time string in format 'HH:mm:ss' (e.g. '00:00:00')
 * @returns The later time string
 */
export function getLaterTime(time1: string, time2: string): string {
    const t1 = dayjs(`1970-01-01T${time1}`);
    const t2 = dayjs(`1970-01-01T${time2}`);
    return t1.isAfter(t2) ? time1 : time2;
}

/**
 * Returns the user's current IANA time zone string as guessed by Day.js.
 *
 * Uses `dayjs.tz.guess()` to determine the most likely time zone of the user's environment.
 *
 * @returns {string} The IANA time zone string (e.g., "Europe/Berlin").
 */
export function getTimeZone(): string {
    return dayjs.tz.guess();
}

/**
 * Converts the given date into the given time zone, keeping the same point in time.
 * In contrast to {@link toLocalDateTime}, this does not reinterpret the input as being
 * in {@param timeZone}, it only changes the zone it is expressed in.
 * @example
 * inTimeZone('2024-08-22T14:37:13.729Z', 'Asia/Tokyo').format('YYYY-MM-DD') // returns '2024-08-22'
 */
export function inTimeZone(date: DateInput, timeZone: string): Dayjs {
    return normalizeDate(date).tz(timeZone);
}

/**
 * Parses a plain time string onto the current date.
 * @param time The time to parse (ex: '08:30:00')
 * @param format The format {@param time} is given in
 * @example
 * parseTimeOfDay('08:30:00').format('HH:mm') // returns '08:30'
 */
export function parseTimeOfDay(time: string, format: 'HH:mm' | 'HH:mm:ss' = 'HH:mm:ss'): Dayjs {
    return dayjs(time, format);
}

/**
 * Returns the ISO 8601 week of the year (1-53) of the given date.
 */
export function getIsoWeek(date: DateInput): number {
    return normalizeDate(date).isoWeek();
}

/**
 * Returns the ISO 8601 week year of the given date. This can differ from the calendar
 * year around the turn of the year, where for example 2024-12-30 belongs to week 1 of 2025.
 */
export function getIsoWeekYear(date: DateInput): number {
    return normalizeDate(date).isoWeekYear();
}

/**
 * Returns the number of ISO 8601 weeks (52 or 53) in the given year.
 * @example
 * getIsoWeeksInYear(2025) // returns 52
 * getIsoWeeksInYear(2026) // returns 53
 */
export function getIsoWeeksInYear(year: number): number {
    return dayjs().year(year).isoWeeksInYear();
}

export type TimeOption = {label: string; totalMinutes: number};

/** Generates the discrete HH:mm time-of-day options at stepHour/stepMinute increments, clamped to minDate/maxDate. */
export function buildTimeOptions({
    minDate = null,
    maxDate = null,
    stepHour = 1,
    stepMinute = 15,
}: {
    minDate?: Date | null;
    maxDate?: Date | null;
    stepHour?: number;
    stepMinute?: number;
} = {}): TimeOption[] {
    const minDayjs = minDate ? dayjs(minDate) : null;
    const maxDayjs = maxDate ? dayjs(maxDate) : null;
    const minTotal = minDayjs ? minDayjs.hour() * 60 + minDayjs.minute() : 0;
    const maxTotal = maxDayjs ? maxDayjs.hour() * 60 + maxDayjs.minute() : 23 * 60 + 59;

    const options: TimeOption[] = [];
    for (let h = 0; h < 24; h += stepHour) {
        for (let m = 0; m <= 59; m += stepMinute) {
            const total = h * 60 + m;
            if (total >= minTotal && total <= maxTotal) {
                options.push({
                    label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
                    totalMinutes: total,
                });
            }
        }
    }
    return options;
}
